import { z } from "zod";
import { particleSchema } from "./particle.js";
import { showConfigSchema } from "./config.js";
import { snapshotSchema } from "./snapshot.js";
import { stageStateSchema } from "./stage.js";

/** Placeholder form payload. Field names and validation land with the real content. */
export const answersSchema = z
  .record(z.string().min(1).max(64), z.string().max(500))
  .refine((v) => Object.keys(v).length <= 10, { message: "at most 10 answers" });
export type Answers = z.infer<typeof answersSchema>;

export const healthResponseSchema = z.object({
  status: z.literal("ok"),
  uptimeMs: z.number().int(),
});

export const readyResponseSchema = z.object({
  status: z.enum(["ok", "degraded"]),
  db: z.enum(["ok", "error"]),
  serverSessionId: z.string().uuid(),
  stage: stageStateSchema,
  round: z.number().int(),
  particles: z.number().int(),
  clients: z.object({ visuals: z.number().int(), admin: z.number().int() }),
});

export const sessionResponseSchema = z.object({
  sessionId: z.string(),
  hasSubmitted: z.boolean(),
  hasReleased: z.boolean(),
  limitReached: z.boolean(),
});

export const createSubmissionBodySchema = z.object({
  answers: answersSchema,
  /** localStorage uuid; the dedupe fallback when cookies are blocked (iOS private mode). */
  clientId: z.string().uuid(),
});

export const createSubmissionResponseSchema = z.object({
  submissionId: z.string().uuid(),
  /** false when the round is closed: the phone moves on to its end screen. */
  canRelease: z.boolean(),
  autoReleased: z.boolean(),
  particle: particleSchema.nullable(),
  stage: stageStateSchema,
});

export const releaseResponseSchema = z.object({
  particle: particleSchema,
  totalParticles: z.number().int(),
  /** Particles in the current round, for the phone's "you are #N of M". */
  roundParticles: z.number().int(),
  threshold: z.number().int(),
  stage: stageStateSchema,
});

export const publicConfigResponseSchema = z.object({
  config: showConfigSchema,
  stage: stageStateSchema,
  round: z.number().int(),
  roundParticles: z.number().int(),
});

export const adminConfigResponseSchema = z.object({ config: showConfigSchema });

export const adminResetBodySchema = z.object({
  /** Guard so a replayed or bookmarked request cannot wipe a live show. */
  confirm: z.literal("reset"),
  keepSubmissions: z.boolean().optional(),
});

export const adminResetResponseSchema = z.object({ snapshot: snapshotSchema });

export const adminSpawnBodySchema = z.object({
  count: z.number().int().min(1).max(100).optional(),
  tone: z.number().min(0).max(1).nullable().optional(),
});

export const adminSpawnResponseSchema = z.object({
  particles: z.array(particleSchema),
  totalParticles: z.number().int(),
  stage: stageStateSchema,
});

export const adminStageResponseSchema = z.object({ stage: stageStateSchema });

export { snapshotSchema as stateResponseSchema };
